import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/axios';
import { Modal } from '@/components';
import Input from '@/components/Input';
import Label from '@/components/Label';
import toast from 'react-hot-toast';
import { TEMPLATE_LIBRARY_QUERY_KEYS } from '../hooks/useTemplateLibraryQuery';

interface AddSubcategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  industryId: string;
  onSuccessAdd?: (subcategoryId: string) => void;
}

export const AddSubcategoryModal: React.FC<AddSubcategoryModalProps> = ({ isOpen, onClose, industryId, onSuccessAdd }) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: { industryId: string; label: string; key: string }) => {
      const response = await axiosInstance.post('/wp-crm/templates/library/subcategories', payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Subcategory added successfully');
      queryClient.invalidateQueries({ queryKey: TEMPLATE_LIBRARY_QUERY_KEYS.all });
      if (onSuccessAdd && data?.data?._id) {
        onSuccessAdd(data.data._id);
      }
      formik.resetForm();
      onClose();
    }
  });

  const formik = useFormik({
    initialValues: {
      label: '',
    },
    validationSchema: Yup.object({
      label: Yup.string().required('Label is required').max(100, 'Max 100 characters'),
    }),
    onSubmit: (values) => {
      const key = values.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      mutation.mutate({
        industryId,
        label: values.label,
        key,
      });
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Add Subcategory"
      actions={[
        {
          label: mutation.isPending ? 'Adding...' : 'Add',
          color: 'primary',
          disabled: mutation.isPending,
          loading: mutation.isPending,
          onClick: () => {
            formik.handleSubmit();
          }
        }
      ]}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
            Subcategory Label
          </Label>
          <Input
            name="label"
            value={formik.values.label}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="e.g. Promotions"
            error={!!(formik.touched.label && formik.errors.label)}
            helperText={formik.touched.label && typeof formik.errors.label === 'string' ? formik.errors.label : undefined}
          />
        </div>
      </div>
    </Modal>
  );
};
